/**
 * Section Reveal Animation
 * Adds fade-in and translate-y animation to sections when they enter viewport
 */
class SectionReveal {
  constructor() {
    this.observer = null;
    this.init();
  }

  /**
   * Animates cards that haven't been animated yet
   * @param {Element} section - The section containing the cards
   */
  #animateCards(section) {
    const cards = section.querySelectorAll(".ud-product-grid__item:not(.card-animated)");
    cards.forEach((card) => {
      // Trigger animation by adding class
      card.classList.add("card-animating");

      // Mark as animated after animation completes
      card.addEventListener(
        "animationend",
        () => {
          card.classList.remove("card-animating");
          card.classList.add("card-animated");
        },
        { once: true }
      );
    });
  }

  /**
   * Sets up MutationObserver to handle newly added cards (for infinite scroll)
   * @param {Element} section - The section to observe
   */
  #setupCardObserver(section) {
    // Only observe if section is already revealed
    if (!section.classList.contains("section-revealed")) return;

    const observer = new MutationObserver((mutations) => {
      let hasNewCards = false;

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE && node instanceof Element) {
            // Check if the added node is a card or contains cards
            const isCard = node.classList.contains("ud-product-grid__item");
            const cards = node.querySelectorAll(".ud-product-grid__item");

            if (isCard || cards.length > 0) {
              hasNewCards = true;
            }
          }
        });
      });

      // Animate new cards if any were added
      if (hasNewCards) {
        requestAnimationFrame(() => {
          this.#animateCards(section);
        });
      }
    });

    // Observe the grid container for new cards
    const grid = section.querySelector('.ud-product-grid, [ref="grid"]');
    if (grid) {
      observer.observe(grid, { childList: true, subtree: true });
    }

    return observer;
  }

  /**
   * Reveals sections that are already in viewport (for DOM patches)
   */
  reinitialize() {
    if (!this.observer) return;

    const sections = document.querySelectorAll(
      "main > .shopify-section:not(.section-revealed), footer:not(.section-revealed), footer > .shopify-section:not(.section-revealed)"
    );

    sections.forEach((section) => {
      const rect = section.getBoundingClientRect();
      const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;

      if (isInViewport) {
        // Reveal immediately if already in viewport
        section.classList.add("section-revealed");
        const hasCards = section.querySelectorAll(".ud-product-grid__item").length > 0;
        if (hasCards) {
          setTimeout(() => this.#animateCards(section), 50);
          this.#setupCardObserver(section);
        }
      } else {
        // Observe normally if not in viewport
        this.observer.observe(section);
      }
    });
  }

  init() {
    // Check if reduced motion is preferred
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      // Skip animation for users who prefer reduced motion
      const sections = document.querySelectorAll("main > .shopify-section, footer, footer > .shopify-section");
      sections.forEach((section) => {
        section.classList.add("section-revealed");
      });
      return;
    }

    // Create intersection observer with optimized settings for smooth animation
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const section = entry.target;
            const hasCards = section.querySelectorAll(".ud-product-grid__item").length > 0;

            // Reveal section first, then trigger card animations
            requestAnimationFrame(() => {
              section.classList.add("section-revealed");

              // If section has cards, ensure they animate after section becomes visible
              if (hasCards) {
                // Wait for section reveal transition to start, then trigger card animations
                setTimeout(() => {
                  this.#animateCards(section);
                }, 150); // Optimal delay for smooth coordination
                // Set up observer for newly added cards (infinite scroll)
                this.#setupCardObserver(section);
              }
            });
            // Unobserve after animation to ensure it only happens once
            this.observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0,
        rootMargin: "0px 0px -50px 0px",
      }
    );

    // Observe all direct child sections under main and footer elements
    const sections = document.querySelectorAll("main > .shopify-section, footer, footer > .shopify-section");
    sections.forEach((section) => {
      this.observer.observe(section);
    });
  }
}

// Initialize when DOM is ready and expose globally
let sectionRevealInstance = null;

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    sectionRevealInstance = new SectionReveal();
    // @ts-ignore
    window.sectionReveal = sectionRevealInstance;
  });
} else {
  sectionRevealInstance = new SectionReveal();
  // @ts-ignore
  window.sectionReveal = sectionRevealInstance;
}
